import { useFormik } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { NewsType } from 'services/newServices'
import { CompareCompanyRequest } from 'services/companyServices'
import { companyServices } from 'services'
import { CompanyAnalysis } from './CompanyAnalysis'

export type CompareCompanyReportProps = {
  handleSetPagePdf: (e: string, type?: boolean) => void
  newsId: string | undefined
  newsDetail: NewsType
  pdfPage?: string
  isOpenDrawer: boolean
}

export type OptionCompany = {
  company_id: number
  company_name: string
  company_code?: string
  same_category: boolean
  has_comparable_report: boolean
  report_id: number | undefined
  news_id: number | undefined
  news_title: string | undefined
  list_news_ai_id: number | undefined
  report_year?: number
}

export const useCompareCompanyReport = (props: CompareCompanyReportProps) => {
  const { id } = useParams()
  const location = useLocation()
  const news_id = props.newsDetail.news_id
  const [isClicked, setIsClicked] = useState(true)
  const [idCopy, setIdCopy] = useState(0)
  const queryParams = new URLSearchParams(location.search)
  const clearCache = queryParams.get('clear_cache') === 'true'
  const [cunrentPage, setCunrentPage] = useState<number>(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isLoadingCompare, setIsLoadingCompare] = useState(false)
  const [index, setIndex] = useState<number>(0)
  const [filterByIndustry, setFilterByIndustry] = useState(true)
  const [pagination, setPagination] = useState({
    page_size: 20,
    page: 1,
    page_count: 100,
    total_count: 0,
  })
  const [selectedCompanies, setSelectedCompanies] = useState<OptionCompany[]>([
    {
      company_id: props.newsDetail.company_id,
      company_name: props.newsDetail.company_name,
      company_code: 'A',
      same_category: true,
      has_comparable_report: true,
      report_id: Number(id),
      news_id: Number(id),
      news_title: props.newsDetail.news_title,
      list_news_ai_id: props.newsDetail.list_news_ai_id,
      report_year: props.newsDetail.company_report?.year,
    },
  ])

  // #region Gets compare a company list
  const [company, setCompany] = useState<OptionCompany[]>([])

  const fetchCompareCompanies = useCallback(
    async (
      params?: Partial<CompareCompanyRequest>,
      isChecked: boolean = false,
    ) => {
      const { data, paginate } = await companyServices.getCompareCompanyList(
        props?.newsDetail?.company_id ?? 0,
        {
          report_type:
            params?.report_type ?? props.newsDetail.company_report?.report_type,
          year: params?.year ?? props.newsDetail.company_report?.year,
          quarter: params?.quarter ?? props.newsDetail.company_report?.quarter,
          page: params?.page ?? 1,
          page_size: params?.page_size ?? 20,
          is_same_industry: params?.is_same_industry ?? true,
          company_name: params?.company_name ?? '',
        },
      )
      if (isChecked) {
        setCompany(data)
      } else {
        setCompany(prev => [...prev].concat(data))
      }
      setPagination(prev => ({ ...prev, ...paginate }))
    },
    [
      props.newsDetail?.company_id,
      props.newsDetail.company_report?.quarter,
      props.newsDetail.company_report?.report_type,
      props.newsDetail.company_report?.year,
    ],
  )

  // #region Gets company analysis?
  const [companyAnalysis, setCompanyAnalysis] =
    useState<CompanyAnalysis | null>(null)
  const [compareError, setCompareError] = useState<boolean>(false)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      news_ids: selectedCompanies.map(x => x.news_id).filter(Boolean),
      // year: props.newsDetail.company_report?.year,
      report_type: props.newsDetail.company_report?.report_type,
      // quarter: props.newsDetail.company_report?.quarter,
    },
    onSubmit: async values => {
      setIsClicked(true)
      setCompanyAnalysis(null)
      setCompareError(false)
      setIsLoadingCompare(true)
      try {
        //await new Promise (resolve => setTimeout(resolve, 200))
        const { data } = await companyServices.getCompareCompanyReport(values)
        setCompanyAnalysis(data)
        setIsLoadingCompare(false)
      } catch (error) {
        setCompareError(true)
        setIsLoadingCompare(false)
      }
    },
  })
  // #endregion

  // #region Summary content
  const [baseNewsId] = useState<string>(id ?? '0')
  const [selectedNewsId, setSelectedNewsId] = useState<string>(id ?? '0')

  const handleSetSelectedNews = (index: number) => {
    const selectedCompany = selectedCompanies[index ?? 0]
    if (!selectedCompany) return
    const newsId = selectedCompany.news_id?.toString() ?? '0'
    setSelectedNewsId(newsId)
  }

  useEffect(() => {}, [selectedNewsId])
  // #endregion

  //#region Scroll select
  const handleScrollBottom = async () => {
    if (isLoadingMore || pagination.page >= pagination.page_count) return
    setIsLoadingMore(true)
    try {
      const nextPage = pagination.page + 1
      await fetchCompareCompanies({
        page: nextPage,
        is_same_industry: filterByIndustry,
      })
    } catch (error) {
    } finally {
      setIsLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchCompareCompanies()
  }, [fetchCompareCompanies])
  // #endregion

  // #region Handle selects/removes a company to compare a report
  const handleSelectCompany = useCallback((option: OptionCompany | null) => {
    if (!option?.company_id || !option.has_comparable_report) return

    setSelectedCompanies(prev => {
      const isExist = prev.find(c => {
        if (option.news_id && c.news_id) {
          return c.news_id === option.news_id
        }
        if (option.report_id && c.report_id) {
          return c.report_id === option.report_id
        }
        return c.company_id === option.company_id
      })
      if (isExist || prev.length >= 5) return prev
      // if (
      //   prev.find(c => c.company_id === option.company_id) ||
      //   prev.length >= 5
      // )
      //   return prev

      const nextCode = String.fromCharCode(65 + prev.length)
      const newCompany = { ...option, company_code: nextCode }
      setIsClicked(false)
      return [...prev, newCompany]
    })
  }, [])

  const handleRemoveCompany = useCallback((idToRemove: number) => {
    setSelectedCompanies(prev => {
      // const newCompanies = prev.filter(c => c.company_id !== company_id)
      const newCompanies = prev.filter(c => {
        if (c.news_id) return c.news_id !== idToRemove
        if (c.report_id) return c.report_id !== idToRemove
        return c.company_id !== idToRemove
      })
      setIsClicked(false)
      setIndex(0)
      return newCompanies.map((c, index) => ({
        ...c,
        company_code: String.fromCharCode(65 + index), // A, B, C...
      }))
    })
  }, [])

  useEffect(() => {}, [selectedCompanies])
  // #endregion

  // #region Detail News by Company
  const [selectedDetailNewsId, setSelectedDetailNewsId] =
    useState<OptionCompany[]>(selectedCompanies)
  let handleSetSelectedDetailNews: (
    company: OptionCompany,
    currentIndex?: number,
  ) => void
  handleSetSelectedDetailNews = useCallback(
    (company: OptionCompany, currentIndex?: number) => {
      setSelectedDetailNewsId(prev => {
        let newList = [...prev]
        const index = newList.findIndex(c => c.news_id === company.news_id)
        if (index !== -1 && currentIndex !== undefined) {
          moveIndex(newList, index, currentIndex)
        } else {
          newList.splice(currentIndex ?? 0, 0, company)
        }
        return newList
      })
    },
    [],
  )

  const moveIndex = (
    optionCompanies: OptionCompany[],
    from: number,
    to: number,
  ) => {
    const [removed] = optionCompanies.splice(from, 1)
    optionCompanies.splice(to, 0, removed)
    return optionCompanies
  }

  const handleRemoveCompanyDetailNews = useCallback(
    (company: OptionCompany) => {
      setSelectedDetailNewsId(prev =>
        prev.filter(c => c.news_id !== company.news_id),
      )
    },
    [],
  )

  useEffect(() => {}, [selectedDetailNewsId])
  // #endregion

  const handleCopy = async (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      await navigator.clipboard.writeText(
        el.innerText
          .replace(/全画面表示/g, '')
          .replace(/コピーする/g, '')
          .replace(/\n{2,}/g, '\n'),
      )
      setIdCopy(Number(id))
      setTimeout(() => {
        setIdCopy(0)
      }, 2000)
    }
  }

  return {
    ...props,
    id,
    formik,
    company,
    selectedCompanies,
    setSelectedCompanies,
    handleSelectCompany,
    handleRemoveCompany,
    clearCache,
    cunrentPage,
    setCunrentPage,
    filterByIndustry,
    setFilterByIndustry,
    selectedNewsId,
    setSelectedNewsId,
    handleSetSelectedNews,
    companyAnalysis,
    compareError,
    selectedDetailNewsId,
    handleSetSelectedDetailNews,
    handleRemoveCompanyDetailNews,
    handleScrollBottom,
    baseNewsId,
    handleCopy,
    news_id,
    isClicked,
    setIsClicked,
    index,
    setIndex,
    fetchCompareCompanies,
    isLoadingCompare,
    idCopy,
  }
}
